import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

const field = "bg-white border-slate-300 text-slate-900";

export const EmailLenderLetter = ({ studyId, onSent }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ to_email: "", to_name: "", message: "" });
  const [lender, setLender] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get(`/portal/studies/${studyId}/lender-letter/defaults`).then((r) => { setForm((f) => ({ ...f, to_email: f.to_email || r.data.to_email, to_name: f.to_name || r.data.to_name })); setLender(r.data.lender_name); }).catch(() => {});
  }, [open, studyId]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post(`/portal/studies/${studyId}/lender-letter/email`, form); toast.success(`Lender letter sent to ${r.data.to_email}${r.data.copy_sent ? "; a copy is in your inbox" : ""}`); setOpen(false); onSent?.(); }
    catch (err) { toast.error(errMsg(err, "Could not send the email")); } finally { setBusy(false); }
  };
  return (
    <>
      <Button data-testid="portal-email-lender-letter" variant="outline" onClick={() => setOpen(true)} className="rounded-full border-cyan-500/50 bg-transparent text-cyan-200 hover:bg-cyan-500/10"><Send className="h-4 w-4 mr-2" />Email to my loan officer</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white text-slate-900 border-slate-200 sm:max-w-lg" data-testid="lender-letter-email-dialog">
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader><DialogTitle className="font-display">Email the lender letter</DialogTitle>
              <DialogDescription className="text-slate-500">Your loan officer{lender ? ` at ${lender}` : ""} gets a secure download link to the 1-page PDF. You'll get a copy.</DialogDescription></DialogHeader>
            <Input required type="email" data-testid="lender-email-to" value={form.to_email} onChange={set("to_email")} className={field} placeholder="Loan officer email *" />
            <Input data-testid="lender-email-name" value={form.to_name} onChange={set("to_name")} className={field} placeholder="Loan officer name" />
            <Textarea data-testid="lender-email-message" rows={4} value={form.message} onChange={set("message")} className={field} placeholder="Optional message, e.g. Here's the feasibility summary for my loan application." />
            <Button type="submit" data-testid="lender-email-send" disabled={busy} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-500 text-white">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send lender letter</Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const opened = (i) => (i.first_opened_at ? `Opened ${i.downloads || 1}x, last ${new Date(i.last_download_at || i.first_opened_at).toLocaleDateString()}` : "Not opened yet");

export const PortalLetterSends = ({ studyId, refresh }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/portal/studies/${studyId}/lender-letter/emails`).then((r) => setItems(r.data)).catch(() => {}); }, [studyId, refresh]);
  if (!items.length) return null;
  return (
    <div className="w-full rounded-xl border border-slate-800 bg-slate-900/60 p-4 mt-2" data-testid="portal-letter-sends">
      <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Lender letter sent</p>
      <ul className="space-y-1 text-sm">{items.map((i) => (
        <li key={i.id} data-testid={`portal-letter-send-${i.id}`} className="text-slate-300">{new Date(i.sent_at).toLocaleDateString()} · {i.to_name || i.to_email} · <span className={i.first_opened_at ? "text-emerald-300" : "text-slate-500"}>{opened(i)}</span></li>
      ))}</ul>
    </div>
  );
};

export const LenderLetterLog = ({ studyId }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/studies/${studyId}/lender-letter-emails`).then((r) => setItems(r.data)).catch(() => {}); }, [studyId]);
  if (!items.length) return null;
  return (
    <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-5 mb-8 no-print" data-testid="lender-letter-log">
      <p className="text-xs uppercase tracking-wider text-cyan-300 font-semibold mb-3">Lender letter sent by the client</p>
      <ul className="space-y-1.5 text-sm">{items.map((i) => (
        <li key={i.id} data-testid={`lender-letter-log-${i.id}`} className="text-slate-300">{new Date(i.sent_at).toLocaleString()} · to {i.to_name ? `${i.to_name} ` : ""}<span className="text-cyan-200">{i.to_email}</span>{i.message ? <span className="text-slate-500"> · "{i.message}"</span> : null} · <span data-testid={`lender-letter-opened-${i.id}`} className={i.first_opened_at ? "text-emerald-300" : "text-slate-500"}>{opened(i)}</span></li>
      ))}</ul>
    </div>
  );
};
